import {
  ACTIVITY_MUTATION_KINDS,
  type ActivityStaffSnapshot,
  type ActivityWaitlistMutationStaffSource,
  type ActivityWaitlistMutationSource,
  type ActivityMutationProvider,
  type ActivityMutationRequest,
  type ActivityMutationState,
  type WaitlistActivityItem,
} from "./activityContracts";
import {
  isActivityIdentifier,
  isValidActivityEpochMs,
  isValidActivityLocalDate,
  isValidActivityTemporalSnapshot,
  parseActivityPointer,
} from "./activityValidation";

export const FRONTEND_ACTIVITY_FIXTURE_CUSTOMER_ID =
  "customer-volera-activity";

type ActivityMutationIdempotencySource = Pick<
  ActivityMutationRequest,
  | "mutation"
  | "pointer"
  | "activityId"
  | "customerAccountId"
  | "providerId"
  | "sourceDataRevision"
  | "sourceEntityRevision"
  | "providerActionId"
  | "providerActionRevision"
>;

/**
 * Produces one bounded, deterministic retry key from the complete immutable
 * mutation authority. Two independent 32-bit accumulators avoid copying
 * provider identifiers into an unbounded request field; this is correlation,
 * not an authentication or authorization primitive.
 */
export function createActivityMutationIdempotencyKey(
  source: ActivityMutationIdempotencySource,
) {
  const canonicalSource = [
    source.mutation,
    source.pointer,
    source.activityId,
    source.customerAccountId,
    source.providerId,
    source.sourceDataRevision,
    source.sourceEntityRevision,
    source.providerActionId,
    source.providerActionRevision,
  ].join("\u001f");
  let first = 0x811c9dc5;
  let second = 0xc6a4a793;

  for (let index = 0; index < canonicalSource.length; index += 1) {
    const codeUnit = canonicalSource.charCodeAt(index);
    first = Math.imul(first ^ codeUnit, 0x01000193) >>> 0;
    second = Math.imul(second ^ codeUnit, 0x85ebca6b) >>> 0;
  }

  return `activity-${source.mutation}-${first
    .toString(16)
    .padStart(8, "0")}${second.toString(16).padStart(8, "0")}`;
}

function freezeWaitlistMutationSource(
  source: ActivityWaitlistMutationSource,
): ActivityWaitlistMutationSource {
  return Object.freeze({
    ...source,
    serviceLines: Object.freeze(
      source.serviceLines.map((line) =>
        Object.freeze({
          ...line,
          staff: Object.freeze({ ...line.staff }),
          addOns: Object.freeze(
            line.addOns.map((addOn) => Object.freeze({ ...addOn })),
          ),
        }),
      ),
    ) as ActivityWaitlistMutationSource["serviceLines"],
    preferences: Object.freeze(
      source.preferences.map((preference) =>
        Object.freeze({ ...preference }),
      ),
    ) as ActivityWaitlistMutationSource["preferences"],
    offer: source.offer
      ? Object.freeze({
          ...source.offer,
          slot: Object.freeze({ ...source.offer.slot }),
        })
      : null,
  });
}

function captureWaitlistMutationStaff(
  staff: ActivityStaffSnapshot,
): ActivityWaitlistMutationStaffSource {
  if (staff.resolution === "specific") {
    return {
      resolution: staff.resolution,
      userLocationId: staff.userLocationId,
      availability: staff.availability,
    };
  }

  if (staff.resolution === "any-professional") {
    return {
      resolution: staff.resolution,
      userLocationId: staff.userLocationId,
      availability: staff.availability,
    };
  }

  return {
    resolution: staff.resolution,
    userLocationId: staff.userLocationId,
    availability: staff.availability,
  };
}

/**
 * Captures the exact provider order and mutation-relevant Waitlist fields into
 * a detached snapshot before any async provider boundary is crossed.
 */
export function createActivityWaitlistMutationSource(
  item: WaitlistActivityItem,
): ActivityWaitlistMutationSource {
  const [firstServiceLine, ...remainingServiceLines] = item.serviceLines;
  const [firstPreference, ...remainingPreferences] = item.preferences;
  const mapServiceLine = (
    line: WaitlistActivityItem["serviceLines"][number],
  ) => ({
    lineId: line.lineId,
    serviceId: line.serviceId,
    variantId: line.variantId,
    staff: captureWaitlistMutationStaff(line.staff),
    addOns: line.addOns.map((addOn) => ({
      groupId: addOn.groupId,
      addOnId: addOn.addOnId,
      quantity: addOn.quantity,
    })),
  });
  const mapPreference = (
    preference: WaitlistActivityItem["preferences"][number],
  ) => ({
    preferenceId: preference.preferenceId,
    localDate: preference.localDate,
    startLocalTime: preference.startLocalTime,
    endLocalTime: preference.endLocalTime,
  });

  return freezeWaitlistMutationSource({
    waitlistReference: item.waitlistReference,
    serviceLines: [
      mapServiceLine(firstServiceLine),
      ...remainingServiceLines.map(mapServiceLine),
    ],
    preferences: [
      mapPreference(firstPreference),
      ...remainingPreferences.map(mapPreference),
    ],
    offer:
      item.offeredSlot.status === "available"
        ? {
            status: "available",
            offerId: item.offeredSlot.offerId,
            offerRevision: item.offeredSlot.offerRevision,
            offeredAtEpochMs: item.offeredSlot.offeredAtEpochMs,
            expiresAtEpochMs: item.offeredSlot.expiresAtEpochMs,
            slotId: item.offeredSlot.slotId,
            slotRevision: item.offeredSlot.slotRevision,
            slot: {
              timeZone: item.offeredSlot.slot.timeZone,
              occurredAtEpochMs:
                item.offeredSlot.slot.occurredAtEpochMs,
              localDate: item.offeredSlot.slot.localDate,
              localTime: item.offeredSlot.slot.localTime,
              durationMinutes:
                item.offeredSlot.slot.durationMinutes,
            },
            handoffId: item.offeredSlot.destination.handoffId,
            handoffRevision:
              item.offeredSlot.destination.handoffRevision,
          }
        : null,
  });
}

function invalidResult(
  request: Pick<ActivityMutationRequest, "mutation" | "pointer">,
  reason: "request-aborted" | "invalid-request",
  retryable: boolean,
): ActivityMutationState {
  return Object.freeze({
    status: "error" as const,
    mutation: request.mutation,
    pointer: request.pointer,
    reason,
    retryable,
  });
}

function isValidMutationRequest(
  request: ActivityMutationRequest,
  evaluatedAtEpochMs: number,
): boolean {
  const parsedPointer = parseActivityPointer(request.pointer);
  const validSourceIdentity =
    parsedPointer !== null &&
    parsedPointer.activityType === request.activityType &&
    parsedPointer.activityId === request.activityId &&
    isActivityIdentifier(request.providerId) &&
    isActivityIdentifier(request.snapshotId) &&
    isActivityIdentifier(request.sourceDataRevision) &&
    isActivityIdentifier(request.sourceEntityRevision) &&
    isActivityIdentifier(request.providerActionRevision);
  const validWaitlistMutation =
    request.mutation === "leave-waitlist" ||
    request.mutation === "book-offered-slot"
      ? request.activityType === "waitlist" &&
        validWaitlistMutationSource(request.waitlistSource) &&
        (request.mutation !== "book-offered-slot" ||
          (request.waitlistSource.offer !== null &&
            request.waitlistSource.serviceLines.every(
              (line) => line.staff.availability === "available",
            ) &&
            isValidActivityEpochMs(evaluatedAtEpochMs) &&
            evaluatedAtEpochMs <
              request.waitlistSource.offer.expiresAtEpochMs &&
            request.providerActionId ===
              request.waitlistSource.offer.handoffId &&
            request.providerActionRevision ===
              request.waitlistSource.offer.handoffRevision))
      : request.waitlistSource === null;

  return (
    (ACTIVITY_MUTATION_KINDS as readonly string[]).includes(
      request.mutation,
    ) &&
    validSourceIdentity &&
    validWaitlistMutation &&
    isActivityIdentifier(request.customerAccountId) &&
    isActivityIdentifier(request.providerActionId) &&
    isActivityIdentifier(request.idempotencyKey) &&
    Boolean(request.signal) &&
    typeof request.signal.aborted === "boolean" &&
    typeof request.signal.addEventListener === "function"
  );
}

function hasExactKeys(
  value: Record<string, unknown>,
  keys: readonly string[],
) {
  const actual = Object.keys(value);
  return (
    actual.length === keys.length && actual.every((key) => keys.includes(key))
  );
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return Boolean(value) && typeof value === "object" && !Array.isArray(value);
}

function isDenseArray(
  value: unknown,
  minimum: number,
  maximum: number,
): value is readonly unknown[] {
  if (
    !Array.isArray(value) ||
    value.length < minimum ||
    value.length > maximum ||
    Object.keys(value).length !== value.length
  ) {
    return false;
  }

  for (let index = 0; index < value.length; index += 1) {
    if (!Object.prototype.hasOwnProperty.call(value, index)) {
      return false;
    }
  }

  return true;
}

function validWaitlistMutationSource(
  value: unknown,
): value is ActivityWaitlistMutationSource {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, [
      "waitlistReference",
      "serviceLines",
      "preferences",
      "offer",
    ]) ||
    !isActivityIdentifier(value.waitlistReference) ||
    !isDenseArray(value.serviceLines, 1, 100) ||
    !isDenseArray(value.preferences, 1, 30)
  ) {
    return false;
  }

  const validServices = value.serviceLines.every(
    (entry) =>
      isRecord(entry) &&
      hasExactKeys(entry, [
        "lineId",
        "serviceId",
        "variantId",
        "staff",
        "addOns",
      ]) &&
      isActivityIdentifier(entry.lineId) &&
      isActivityIdentifier(entry.serviceId) &&
      (entry.variantId === null || isActivityIdentifier(entry.variantId)) &&
      validWaitlistMutationStaffSource(entry.staff) &&
      isDenseArray(entry.addOns, 0, 100) &&
      entry.addOns.every(
        (addOn) =>
          isRecord(addOn) &&
          hasExactKeys(addOn, [
            "groupId",
            "addOnId",
            "quantity",
          ]) &&
          isActivityIdentifier(addOn.groupId) &&
          isActivityIdentifier(addOn.addOnId) &&
          Number.isSafeInteger(addOn.quantity) &&
          Number(addOn.quantity) > 0 &&
          Number(addOn.quantity) <= 20,
      ),
  );
  const localTimePattern = /^(?:[01]\d|2[0-3]):[0-5]\d$/u;
  const validPreferences = value.preferences.every(
    (entry) =>
      isRecord(entry) &&
      hasExactKeys(entry, [
        "preferenceId",
        "localDate",
        "startLocalTime",
        "endLocalTime",
      ]) &&
      isActivityIdentifier(entry.preferenceId) &&
      isValidActivityLocalDate(entry.localDate) &&
      ((entry.startLocalTime === null && entry.endLocalTime === null) ||
        (typeof entry.startLocalTime === "string" &&
          typeof entry.endLocalTime === "string" &&
          localTimePattern.test(entry.startLocalTime) &&
          localTimePattern.test(entry.endLocalTime) &&
          entry.startLocalTime < entry.endLocalTime)),
  );
  const validOffer =
    value.offer === null ||
    (isRecord(value.offer) &&
      hasExactKeys(value.offer, [
        "status",
        "offerId",
        "offerRevision",
        "offeredAtEpochMs",
        "expiresAtEpochMs",
        "slotId",
        "slotRevision",
        "slot",
        "handoffId",
        "handoffRevision",
      ]) &&
      value.offer.status === "available" &&
      isActivityIdentifier(value.offer.offerId) &&
      isActivityIdentifier(value.offer.offerRevision) &&
      isValidActivityEpochMs(value.offer.offeredAtEpochMs) &&
      isValidActivityEpochMs(value.offer.expiresAtEpochMs) &&
      Number(value.offer.expiresAtEpochMs) >
        Number(value.offer.offeredAtEpochMs) &&
      isActivityIdentifier(value.offer.slotId) &&
      isActivityIdentifier(value.offer.slotRevision) &&
      isValidActivityTemporalSnapshot(value.offer.slot) &&
      value.offer.slot.localTime !== null &&
      value.offer.slot.durationMinutes !== null &&
      Number(value.offer.expiresAtEpochMs) <=
        value.offer.slot.occurredAtEpochMs &&
      isActivityIdentifier(value.offer.handoffId) &&
      isActivityIdentifier(value.offer.handoffRevision));

  return validServices && validPreferences && validOffer;
}

function validWaitlistMutationStaffSource(
  value: unknown,
): value is ActivityWaitlistMutationStaffSource {
  if (
    !isRecord(value) ||
    !hasExactKeys(value, ["resolution", "userLocationId", "availability"])
  ) {
    return false;
  }

  if (value.resolution === "specific") {
    return (
      value.availability === "available" &&
      isActivityIdentifier(value.userLocationId)
    );
  }

  if (value.resolution === "any-professional") {
    return (
      value.availability === "available" && value.userLocationId === null
    );
  }

  return (
    value.resolution === "unavailable" &&
    value.availability === "unavailable" &&
    (value.userLocationId === null ||
      isActivityIdentifier(value.userLocationId))
  );
}

/**
 * Builds the frontend-only Activity mutation boundary around the exact
 * currently authenticated customer. The contract can validate an injected
 * live provider success, but this fallback never publishes one.
 */
export function createFrontendFailClosedActivityMutationProvider(
  readCurrentCustomerAccountId: () => string | null,
  readCurrentEpochMs: () => number = Date.now,
): ActivityMutationProvider {
  return Object.freeze({
    async performActivityMutation(
      request: ActivityMutationRequest,
    ): Promise<ActivityMutationState> {
      if (!isValidMutationRequest(request, readCurrentEpochMs())) {
        return invalidResult(request, "invalid-request", false);
      }

      if (request.signal.aborted) {
        return invalidResult(request, "request-aborted", true);
      }

      const activeCustomerAccountId =
        readCurrentCustomerAccountId();

      if (
        activeCustomerAccountId === null ||
        request.customerAccountId !== activeCustomerAccountId
      ) {
        return Object.freeze({
          status: "customer-mismatch" as const,
          mutation: request.mutation,
          pointer: request.pointer,
          reason: "customer-mismatch" as const,
        });
      }

      // No mutation, including Waitlist leave/offer conversion, has an
      // authorized backend transport in this frontend-only rebuild. A live
      // injected provider may publish a strictly correlated success result.
      return Object.freeze({
        status: "unsupported" as const,
        mutation: request.mutation,
        pointer: request.pointer,
        reason: "frontend-only-provider" as const,
      });
    },
  });
}

export const frontendFailClosedActivityMutationProvider =
  createFrontendFailClosedActivityMutationProvider(
    () => FRONTEND_ACTIVITY_FIXTURE_CUSTOMER_ID,
  );
